import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, photoSrc } from "@/components/Avatar";
import { VideoPlayer } from "@/components/video/VideoPlayer";
import { ShareCard } from "@/components/community/ShareToCommunity";
import { ReactionBar, Comments, CommentsToggle } from "@/components/community/PostSocial";
import { ExpandableText } from "@/components/feed/ExpandableText";
import { RelativeTime } from "@/components/feed/RelativeTime";
import { PostMenu } from "@/components/feed/PostMenu";

const GRID = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-2" };

export function FeedCard({ item, user, onRemoved }) {
  const testid = `feed-item-${item.type}-${item.id}`;
  const base = item.type === "community" ? "community" : "profile";
  return (
    <article className="bg-white rounded-2xl border border-zinc-200 p-4" data-testid={testid} data-type={item.type}>
      <header className="flex items-start gap-2.5">
        <Avatar src={item.system_tag ? null : item.author?.photo_url} name={item.author?.name} size={38} testId={`${testid}-avatar`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-sm font-semibold truncate">
              {item.system_tag ? item.author?.name : <Link to={`/membro/${item.author?.id}`} className="hover:underline" data-testid={`${testid}-author`}>{item.author?.name}</Link>}
            </span>
            <SourceChip item={item} />
          </div>
          <RelativeTime iso={item.created_at} edited={!!item.edited_at} testid={`${testid}-time`} />
        </div>
        <PostMenu item={item} user={user} onRemoved={onRemoved} />
      </header>

      <ExpandableText text={item.text} testid={`${testid}-text`} className="mt-2" />
      {item.share && <ShareCard share={item.share} />}
      {item.video?.id && <VideoPlayer video={item.video} testid={`${testid}-video`} />}
      {item.images?.length > 0 && <ImageGrid images={item.images} testid={`${testid}-images`} />}

      <SocialRow item={item} user={user} base={base} />
    </article>
  );
}

function SourceChip({ item }) {
  const s = item.source;
  const isCommunity = s.type === "community";
  const label = isCommunity ? (s.kind === "group" ? "Grupo" : "") : (s.on_profile === false ? "Feed" : "Perfil");
  const link = isCommunity ? s.link : (s.on_profile === false ? item.post_link : s.link);
  return (
    <Link to={link} data-testid={`feed-source-${item.type}-${item.id}`} title={isCommunity ? s.name : (s.on_profile === false ? "Publicação no feed geral" : "Publicação de perfil")}
          className={`inline-flex items-center gap-1 shrink min-w-0 max-w-[55%] text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${isCommunity ? "bg-[#c8102e]/10 text-[#c8102e]" : "bg-zinc-100 text-zinc-600"}`}>
      {isCommunity ? <Users size={11} className="shrink-0" /> : <UserCircle2 size={11} className="shrink-0" />}
      <span className="truncate">{isCommunity ? (label ? `${label} · ${s.name}` : s.name) : label}</span>
    </Link>
  );
}

function ImageGrid({ images, testid }) {
  return (
    <div className={`mt-2 grid gap-1.5 ${GRID[images.length] || "grid-cols-3"}`} data-testid={testid}>
      {images.map((im, i) => {
        const url = typeof im === "string" ? im : im.url;
        return (
          <a key={i} href={photoSrc(url)} target="_blank" rel="noopener noreferrer" className={`block overflow-hidden rounded-xl bg-zinc-100 ${images.length === 1 ? "" : "aspect-square"}`}>
            <img src={photoSrc(url)} alt="" loading="lazy" className={images.length === 1 ? "w-full h-auto max-h-[60vh] object-contain" : "w-full h-full object-cover"} />
          </a>
        );
      })}
    </div>
  );
}

function SocialRow({ item, user, base }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(item.comments_count || 0);
  const isCommunity = base === "community";
  const refresh = () => api.get(isCommunity ? `/community-posts/${item.id}` : `/profile/posts/${item.id}`).then((r) => setCount(r.data.comments_count)).catch(() => {});
  return (
    <>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <ReactionBar targetType="post" targetId={item.id} reactions={item.reactions} mine={item.mine} canReact={item.can_interact} base={base} />
        <CommentsToggle count={count} open={open} onToggle={() => setOpen((o) => !o)} postId={item.id} />
      </div>
      {open && <Comments post={item} user={user} canInteract={item.can_interact} isStaff={user?.role === "admin"} isPostOwner={!isCommunity && item.is_mine} base={base} onCount={refresh}
                         contextLabel={isCommunity ? item.source?.name : (item.source?.on_profile === false ? "Feed geral" : "Perfil / Feed geral")} />}
      {isCommunity && !item.can_interact && (
        <Link to={item.source.link} className="mt-2 inline-block text-xs font-semibold text-[#c8102e]" data-testid={`feed-join-${item.id}`}>Entrar na comunidade para interagir</Link>
      )}
    </>
  );
}
